import React from 'react';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import classNames from 'classnames';
import './index.scss';

export interface IImageListProps {
  /** 数据列表 */
  list: any[];
  /** 展示个数，超出部分不显示，默认展示全部 */
  maxCount?: number;
  /** 默认占位图，默认 DefaultImg.VIDEO */
  defaultImage?: string;
  /** key 字段名，默认 'video_id' */
  keyField?: string;
  /** 图片URL字段名，默认 'video_image' */
  imageField?: string;
  /** 容器自定义 className */
  className?: string;
  /** 容器自定义 style */
  style?: React.CSSProperties;
  /** 是否展示播放图标覆盖层 */
  showPlayIcon?: boolean;
  /** 点击事件，参数为被点击的 item 和 index */
  onClick?: (item: any, index: number) => void;
}

/**
 * ImageList - 图片列表组件
 *
 * 封装一组图片的渲染，支持卡片堆叠展示、播放图标覆盖层、点击事件等。
 *
 * @example 基础用法
 * ```tsx
 * <ImageList
 *   list={videoList}
 *   maxCount={3}
 *   keyField="video_id"
 *   imageField="video_image"
 *   onClick={(item) => handleVideoClick(item)}
 * />
 * ```
 */
export default function ImageList({
  list,
  maxCount,
  defaultImage = DefaultImg.VIDEO,
  keyField = 'video_id',
  imageField = 'video_image',
  className,
  style,
  showPlayIcon = false,
  onClick,
}: IImageListProps) {
  if (!list || list.length === 0) {
    return null;
  }

  const displayList = maxCount ? list.slice(0, maxCount) : list;
  const clickable = !!onClick;

  return (
    <div
      className={classNames('dbom-image-list', className, {
        'dbom-image-list--play-icon': showPlayIcon,
      })}
      style={style}
    >
      {displayList.map((item: any, index: number) => (
        <RenderImg
          key={item[keyField] ?? index}
          className={classNames('dbom-image-list-img', {
            'dbom-image-list-img--clickable': clickable,
          })}
          coverImage={item[imageField]}
          defaultImage={defaultImage}
          onClick={() => onClick?.(item, index)}
        />
      ))}
    </div>
  );
}
